<script setup lang="ts">
import { useHead, useRouter } from '#app';
import { useSpotlight } from '@/composables/useSpotlight';
import { provideSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';
import { useSeoLocaleAvailability } from '@/composables/useSeoLocaleAvailability';
import AppNav from '@/components/AppNav.vue';
import AppFooter from '@/components/AppFooter.vue';
import FeaturesRoles from '@/components/features/FeaturesRoles.vue';
import { computed, ref } from 'vue';
import { i18n, t } from '@/i18n';
import {
  applySpotlightListConfigLocale,
  applySpotlightPostsListLocale,
} from '@/lib/spotlight-locale';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';
import {
  SPOTLIGHT_CATEGORY_DEFINITIONS,
  spotlightCategoryByValue,
  type SpotlightCategoryValue,
} from '@/lib/spotlight-taxonomy';

const props = defineProps<{
  category?: SpotlightCategoryValue;
}>();

interface BlogCategory {
  title: string;
}

interface BlogAuthor {
  name: string;
  avatar?: string;
}

interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  excerpt?: any[]; // Portable Text array
  image?: string;
  publishedAt?: string;
  categories?: BlogCategory[];
  category?: string;
  tags?: string[];
  readTime?: number;
  author?: BlogAuthor;
}

const router = useRouter();
const route = useRoute();
const { fetchSpotlightPosts, fetchSpotlightListConfig } = useSpotlight();
const { fetchAvailableLocales } = useSeoLocaleAvailability();
const locale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const selectedCategory = computed(() => spotlightCategoryByValue(props.category));
const allCategoryLabel = computed(() => t('ui.spotlight.allCategories'));
const sectionPath = computed(() =>
  selectedCategory.value ? `/spotlight/categories/${selectedCategory.value.slug}` : '/spotlight'
);

const { data: listConfig } = await useAsyncData(
  () => `spotlight:index:list-config:${locale.value}`,
  async () => {
    try {
      return await fetchSpotlightListConfig(locale.value);
    } catch (error) {
      console.error('Error fetching spotlight list config:', error);
      return null;
    }
  },
  { server: true, lazy: false, default: () => null, watch: [locale] }
);

const localizedListConfig = computed(() => {
  void i18n.locale;
  if (!listConfig.value) return null;
  return locale.value === 'en'
    ? applySpotlightListConfigLocale(listConfig.value)
    : listConfig.value.exists && listConfig.value.language === locale.value
      ? listConfig.value
      : null;
});

const heroTitle = computed(
  () =>
    (selectedCategory.value ? t(selectedCategory.value.labelKey) : '') ||
    localizedListConfig.value?.title ||
    t('landingPages.spotlight.index.hero.title_fallback')
);
const heroSubtitle = computed(
  () =>
    localizedListConfig.value?.subtitle || t('landingPages.spotlight.index.hero.subtitle_fallback')
);
const faqSectionTitle = computed(
  () => localizedListConfig.value?.faqTitle || t('landingPages.spotlight.index.faq.title_fallback')
);
const faqItemsFromSanity = computed(() => localizedListConfig.value?.faqItems || []);

// Pagination settings
const postsPerPage = 9;
const currentPage = ref(1); // Always page 1 for index

// Fetch blog posts from Sanity
const { data: allPosts } = await useAsyncData<BlogPost[]>(
  () => `spotlight:index:posts:${locale.value}`,
  async () => {
    try {
      const data = await fetchSpotlightPosts(locale.value);
      return (data || []) as BlogPost[];
    } catch (error) {
      console.error('Error fetching spotlight posts:', error);
      return [];
    }
  },
  {
    server: true,
    lazy: false,
    default: () => [],
    watch: [locale],
  }
);
const { data: availableLocales } = await useAsyncData(
  'spotlight-index-locale-availability',
  () => fetchAvailableLocales('spotlight'),
  { server: true, lazy: false, default: () => [] }
);

// Pagination calculations
const totalPosts = computed(() => allPosts.value?.length || 0);
const headLocales = computed(() =>
  availableLocales.value.length
    ? availableLocales.value
    : totalPosts.value > 0
      ? [locale.value]
      : []
);
const languagePaths = computed(() =>
  Object.fromEntries(
    headLocales.value.map((availableLocale) => [
      availableLocale,
      localizedPath(availableLocale, sectionPath.value),
    ])
  )
);
provideSeoDetailLanguagePaths(languagePaths);
const startIndex = computed(() => (currentPage.value - 1) * postsPerPage);
const endIndex = computed(() => startIndex.value + postsPerPage);
const localizedPosts = computed(() => {
  void i18n.locale;
  return locale.value === 'en'
    ? applySpotlightPostsListLocale(allPosts.value || [])
    : allPosts.value || [];
});
const visiblePosts = computed(() => {
  const category = selectedCategory.value?.value;
  return category
    ? localizedPosts.value.filter((post) => post.category === category)
    : localizedPosts.value;
});
const visiblePostCount = computed(() => visiblePosts.value.length);
const hasPublishedLocaleContent = computed(
  () => visiblePostCount.value > 0 && headLocales.value.includes(locale.value)
);
const totalPages = computed(() =>
  selectedCategory.value ? 1 : Math.ceil(visiblePostCount.value / postsPerPage)
);
const categoryLinks = computed(() =>
  SPOTLIGHT_CATEGORY_DEFINITIONS.flatMap((category) => {
    const count = localizedPosts.value.filter((post) => post.category === category.value).length;
    return count > 0
      ? [
          {
            ...category,
            count,
            label: t(category.labelKey),
            path: localizedPath(locale.value, `/spotlight/categories/${category.slug}`),
          },
        ]
      : [];
  })
);
const localizedCategoryLabel = (value?: string) =>
  (spotlightCategoryByValue(value) ? t(spotlightCategoryByValue(value)!.labelKey) : value) ||
  t('landingPages.spotlight.index.hero.title_fallback');

const paginatedPosts = computed(() => {
  if (!visiblePosts.value.length) return [];
  return selectedCategory.value
    ? visiblePosts.value
    : visiblePosts.value.slice(startIndex.value, endIndex.value);
});
const visibleRangeEnd = computed(() =>
  selectedCategory.value ? visiblePostCount.value : Math.min(endIndex.value, visiblePostCount.value)
);

// Navigation functions
function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  if (page === 1) {
    router.push(localizedPath(locale.value, '/spotlight'));
  } else {
    router.push(localizedPath(locale.value, `/spotlight/page/${page}`));
  }
  // Scroll to top
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
}

// Generate page numbers for pagination buttons
const pageNumbers = computed(() => {
  const pages: (number | string)[] = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    // Show pages around current page
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push('...');
    }

    // Always show last page
    pages.push(total);
  }

  return pages;
});

const pageTitle = computed(
  () =>
    (selectedCategory.value
      ? `${t(selectedCategory.value.labelKey)} | Spotlight`
      : localizedListConfig.value?.seoTitle) || t('landingPages.spotlight.index.seo.title_fallback')
);
const pageDescription = computed(
  () =>
    localizedListConfig.value?.seoDescription ||
    t('landingPages.spotlight.index.seo.description_fallback')
);

// Canonical URL - each page should have its own canonical URL
const canonicalUrl = computed(() => {
  return absoluteLocalizedUrl(locale.value, sectionPath.value);
});

// Generate prev/next URLs for pagination
const prevUrl = computed(() => {
  // Page 1 doesn't have previous
  return null;
});

const nextUrl = computed(() => {
  if (!selectedCategory.value && totalPages.value > 1) {
    return absoluteLocalizedUrl(locale.value, '/spotlight/page/2');
  }
  return null;
});

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => {
  const links: Array<{ rel: string; href: string }> = [
    { rel: 'canonical', href: canonicalUrl.value },
    ...(hasPublishedLocaleContent.value
      ? buildPathHreflangLinks(sectionPath.value, 'https://veeso.ai', headLocales.value)
      : []),
  ];

  // Add prev/next links for pagination (helps with SEO and prevents orphan pages)
  if (prevUrl.value) {
    links.push({ rel: 'prev', href: prevUrl.value });
  }
  if (nextUrl.value) {
    links.push({ rel: 'next', href: nextUrl.value });
  }

  return {
    title: `${pageTitle.value} | Veeso`,
    htmlAttrs: { lang: locale.value },
    meta: [
      ...(!hasPublishedLocaleContent.value ? [{ name: 'robots', content: 'noindex,follow' }] : []),
      { name: 'description', content: pageDescription.value },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: pageDescription.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:locale', content: ogLocaleFor(locale.value) },
      { property: 'og:image', content: ogImageUrl.value },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle.value },
      { name: 'twitter:image', content: ogImageUrl.value },
    ],
    link: links,
  };
});

function formatDate(dateString: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dateLocale =
    {
      en: 'en-US',
      ja: 'ja-JP',
      ko: 'ko-KR',
      es: 'es-ES',
    }[locale.value] || 'en-US';
  return date.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const faqItems = computed(() => faqItemsFromSanity.value);

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="spotlight-page min-h-screen bg-white" :data-locale="locale">
    <!-- Header -->
    <AppNav active-path="/spotlight" />

    <!-- Hero Section -->
    <section
      class="relative pt-48 pb-32 overflow-hidden learn-hero"
      :aria-label="t('ui.a11y.heroSection')"
    >
      <div class="relative container mx-auto px-6 md:px-4 text-center z-10">
        <h1
          class="font-boldonse text-5xl md:text-7xl text-white uppercase tracking-tight mb-8 drop-shadow-sm"
        >
          {{ heroTitle }}
        </h1>
        <p
          v-if="heroSubtitle"
          class="font-boldonse text-white/90 text-xl max-w-2xl mx-auto uppercase"
        >
          {{ heroSubtitle }}
        </p>
      </div>
    </section>

    <main class="relative z-[1]">
      <!-- Blog Posts Grid -->
      <section
        class="max-w-[1200px] mx-auto px-4 md:px-8 pt-24 pb-24"
        :aria-label="t('ui.a11y.spotlightPosts')"
      >
        <nav
          v-if="categoryLinks.length > 0"
          class="mb-12 flex flex-wrap items-center justify-center gap-3"
          :aria-label="t('ui.a11y.spotlightCategories')"
        >
          <NuxtLink
            :to="localizedPath(locale, '/spotlight')"
            class="rounded-full border px-5 py-2 text-sm font-semibold no-underline transition-colors"
            :class="
              !selectedCategory
                ? 'border-black bg-black text-white'
                : 'border-black/10 text-black/60 hover:border-black/30 hover:text-black'
            "
          >
            {{ allCategoryLabel }}
          </NuxtLink>
          <NuxtLink
            v-for="spotlightCategory in categoryLinks"
            :key="spotlightCategory.value"
            :to="spotlightCategory.path"
            class="rounded-full border px-5 py-2 text-sm font-semibold no-underline transition-colors"
            :class="
              selectedCategory?.value === spotlightCategory.value
                ? 'border-black bg-black text-white'
                : 'border-black/10 text-black/60 hover:border-black/30 hover:text-black'
            "
          >
            {{ spotlightCategory.label }} · {{ spotlightCategory.count }}
          </NuxtLink>
        </nav>

        <div
          v-if="paginatedPosts && Array.isArray(paginatedPosts) && paginatedPosts.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <NuxtLink
            v-for="post in paginatedPosts"
            v-slot="{ href, navigate }"
            :key="post._id"
            custom
            :to="localizedPath(locale, `/spotlight/${encodeURIComponent(post.slug)}`)"
          >
            <a
              :href="href"
              class="flex flex-col bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02] shadow-[0_3px_7px_0px_rgba(0,0,0,0.05)] cursor-pointer no-underline text-inherit"
              @click="navigate"
            >
              <!-- Featured Image -->
              <div v-if="post?.image" class="h-48 relative overflow-hidden bg-black/5">
                <img
                  :src="post.image"
                  :alt="post?.title || t('landingPages.spotlight.index.image_alt_fallback')"
                  class="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>

              <!-- Content -->
              <div class="flex-1 flex flex-col p-6">
                <!-- Category & Meta -->
                <div class="flex items-center gap-3 mb-3">
                  <span
                    v-if="
                      post?.category ||
                      (post?.categories &&
                        Array.isArray(post.categories) &&
                        post.categories.length > 0)
                    "
                    class="text-xs font-medium px-3 py-1 rounded-full bg-black/5 text-black/60"
                  >
                    {{ localizedCategoryLabel(post.category || post.categories?.[0]?.title) }}
                  </span>
                  <span v-if="post?.readTime" class="text-xs text-black/50">
                    {{ t('landingPages.spotlight.index.min_read', { count: post.readTime }) }}
                  </span>
                </div>

                <!-- Title -->
                <h3 class="text-xl md:text-2xl font-normal text-black/90 mb-3 line-clamp-2">
                  {{ post?.title }}
                </h3>

                <!-- Excerpt -->
                <p
                  v-if="post?.excerpt"
                  class="text-base text-black/60 font-normal leading-relaxed mb-4 line-clamp-3"
                >
                  {{
                    Array.isArray(post.excerpt)
                      ? post.excerpt
                          .map((block: any) => {
                            if (block._type === 'block' && block.children) {
                              return block.children
                                .map((child: any) => {
                                  if (typeof child === 'string') return child;
                                  if (child.text) return child.text;
                                  return '';
                                })
                                .join('');
                            }
                            return '';
                          })
                          .join(' ')
                          .trim()
                      : post.excerpt
                  }}
                </p>

                <!-- Author & Date -->
                <div class="mt-auto pt-4 flex items-center justify-between">
                  <div v-if="post?.author" class="flex items-center gap-2">
                    <img
                      v-if="post.author?.avatar"
                      :src="post.author.avatar"
                      :alt="
                        post.author?.name || t('landingPages.spotlight.index.author_alt_fallback')
                      "
                      class="w-8 h-8 rounded-full"
                    />
                    <span class="text-sm text-black/70">
                      {{ post.author?.name }}
                    </span>
                  </div>
                  <span v-if="post?.publishedAt" class="text-sm text-black/50">
                    {{ formatDate(post.publishedAt) }}
                  </span>
                </div>

                <!-- Read More -->
                <div class="mt-4 pt-4 border-t border-black/5">
                  <span class="text-sm font-medium text-black/80 inline-flex items-center gap-2">
                    {{ t('landingPages.spotlight.index.card.read_more') }}
                    <span class="transition-transform group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </div>
            </a>
          </NuxtLink>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-24">
          <p class="text-lg text-black/60">{{ t('landingPages.spotlight.index.empty_state') }}</p>
        </div>

        <!-- Pagination Controls -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-center gap-2 mt-12"
          :aria-label="t('ui.a11y.pagination')"
        >
          <!-- Previous Button -->
          <button
            @click="prevPage"
            :disabled="currentPage === 1"
            :aria-label="t('landingPages.spotlight.index.pagination.prev_aria')"
            class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          >
            {{ t('landingPages.spotlight.index.pagination.prev') }}
          </button>

          <!-- Page Numbers -->
          <div class="flex items-center gap-1">
            <button
              v-for="(pageNum, index) in pageNumbers"
              :key="`page-${index}`"
              @click="typeof pageNum === 'number' ? goToPage(pageNum) : null"
              :disabled="typeof pageNum === 'string'"
              :aria-label="
                typeof pageNum === 'number'
                  ? t('landingPages.spotlight.index.page_aria', { page: String(pageNum) })
                  : undefined
              "
              :aria-current="pageNum === currentPage ? 'page' : undefined"
              class="min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:cursor-default"
              :class="
                pageNum === currentPage
                  ? 'bg-black/90 text-white'
                  : typeof pageNum === 'string'
                    ? 'text-black/30 cursor-default'
                    : 'bg-white text-black/70 hover:bg-black/5'
              "
            >
              {{ pageNum }}
            </button>
          </div>

          <!-- Next Button -->
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            :aria-label="t('landingPages.spotlight.index.pagination.next_aria')"
            class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          >
            {{ t('landingPages.spotlight.index.pagination.next') }}
          </button>
        </div>

        <!-- Pagination Info -->
        <div v-if="visiblePostCount > 0" class="text-center mt-6 text-sm text-black/50">
          {{
            t('landingPages.spotlight.index.showing_posts', {
              start: String(startIndex + 1),
              end: String(visibleRangeEnd),
              total: String(visiblePostCount),
            })
          }}
        </div>
      </section>

      <!-- FAQ Section -->
      <section v-if="faqItems.length > 0" class="py-24 bg-white">
        <div class="container mx-auto px-6 md:px-4">
          <div class="max-w-[800px] mx-auto">
            <h2
              class="font-boldonse text-[clamp(1.5rem,3vw,2rem)] text-black/90 uppercase leading-[1.6] mb-12 text-center"
            >
              {{ faqSectionTitle }}
            </h2>
            <div class="space-y-4">
              <div
                v-for="(item, index) in faqItems"
                :key="index"
                class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
              >
                <button
                  type="button"
                  @click="toggleFaq(index)"
                  class="flex items-center justify-between w-full text-left font-medium text-xl text-black/90 focus:outline-none"
                >
                  <h3 class="font-medium text-xl text-black/90">{{ item.question }}</h3>
                  <span
                    class="transform transition-transform duration-300 text-2xl"
                    :class="{ 'rotate-45': expandedFaqIndex === index }"
                  >
                    +
                  </span>
                </button>
                <div
                  class="grid transition-all duration-300 ease-in-out overflow-hidden"
                  :class="
                    expandedFaqIndex === index
                      ? 'grid-rows-[1fr] opacity-100 mt-4'
                      : 'grid-rows-[0fr] opacity-0'
                  "
                >
                  <div class="overflow-hidden">
                    <p class="text-black/60 leading-relaxed text-lg" v-html="item.answer" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <FeaturesRoles />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
.learn-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.learn-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}
</style>
